import { useQuery } from "@tanstack/react-query";
import useCredits from "features/User/hooks/useCredits";
import {
  Badges,
  getStudyDetailsResponse,
  StudyDetailsResponse,
} from "helpers/api";
import {
  isStudySnapshotEmpty,
  parseStudyDetailsResponse,
} from "helpers/studyDetail";
import { useEffect, useState } from "react";

/**
 * Hook to fetch study details for a given paper or return details about the user that
 * prevented the fields from being fetched.
 *
 * @param paperId id of the paper for the study detail
 * @param badges badges for the paper to be used in parsing
 * @param fetchOnMount whether the fetch for snapshot details should automatically occur on mount
 * @param isTest whether the query should be run in test mode
 * @returns Parsed study details as study snapshot fields
 */

interface UseStudySnapshotFieldsProps {
  paperId: string;
  badges?: Badges;
  fetchOnMount?: boolean;
  isTest?: boolean;
  spendCredit?: boolean;
}

const useStudySnapshotFields = ({
  paperId,
  badges,
  fetchOnMount = false,
  isTest = false,
  spendCredit = true,
}: UseStudySnapshotFieldsProps) => {
  const [enableFetch, setEnableFetch] = useState(fetchOnMount);

  const {
    isOutOfSnapshotCredits,
    isPremium,
    isNotSignedIn,
    spendCreditOnStudyDetails,
    wasStudySnapshotPreviouslyRunOnPaperId,
  } = useCredits();

  const previouslyRunPaperId = wasStudySnapshotPreviouslyRunOnPaperId(paperId);

  const isOutOfCredits =
    !isPremium && isOutOfSnapshotCredits && !previouslyRunPaperId;

  const isEnabled = !isOutOfCredits && enableFetch;

  // Define the query using react-query's useQuery
  const { data, error, isLoading } = useQuery(
    ["studyDetails", paperId],
    async () => {
      const response = await getStudyDetailsResponse(paperId);
      return response;
    },
    {
      enabled: isEnabled,
      staleTime: isTest ? 0 : Infinity,
      retry: isTest ? false : 3,
      onSuccess: (data: StudyDetailsResponse) => {
        if (
          spendCredit &&
          !previouslyRunPaperId &&
          !isStudySnapshotEmpty(data, badges)
        ) {
          spendCreditOnStudyDetails(paperId);
        }
      },
    }
  );

  useEffect(() => {
    (async () => {
      if (enableFetch == false) {
        if (previouslyRunPaperId || fetchOnMount) {
          setEnableFetch(true);
        }
      }
    })();
    return () => {};
  }, [
    paperId,
    fetchOnMount,
    previouslyRunPaperId,
    isPremium,
    setEnableFetch,
    enableFetch,
  ]);

  return {
    fields: !data ? null : parseStudyDetailsResponse(data, badges),
    fieldsError: error,
    isLoading: isLoading && isEnabled,
    isOutOfCredits,
    isNotSignedIn,
    handleGenerateSnapshot: () => setEnableFetch(true),
    dailyLimitReached: data?.dailyLimitReached,
  };
};

export default useStudySnapshotFields;
